<template>
     <div class="video-message-container">
        <div class="header-message">
            <p>Video Message ({{ templateOrder + 1 }}/3)</p>
            <div @click="removeVideoTemplate">
                <i class="bx bx-trash"></i>
            </div>
        </div>
        <div class="upload-media-container">
          <ValidationProvider
            :rules="isVideoFromCopiedMessage ? null : {
              required: {
                value: true,
                message: 'Please upload a video.',
              },
              multiCustom: {
                callbacks: [isVideoTypeValid, isVideoSizeValid],
                messages: [
                  'The file upload must be in MP4, MOV, or WMV format',
                  `The maximum file upload size is ${maxVideoSize} MB`,
                ],
              }
            }"
            v-slot="{ errors }"
            ref="videoValidationProviderRef"
          >
          <div v-if="isUploadingVideo" class="uploading-container">
            <div class="loading-section">
              <div class="spinner-border" role="status"></div>
            </div>
          </div>
          <div v-else class="message-content">
              <div v-if="videoUrl === null" class="dropzone-container">
                  <vue-dropzone
                      ref="videoDropzoneRef"
                      id="video-dropzone"
                      :options="dropzoneOptions"
                      :useCustomSlot=true
                      @vdropzone-file-added="handleAddedVideo"
                  >
                  <div class="dropzone-custom-input">
                      <div>
                          <i class="bx bx-video"></i>
                      </div>
                      <p class="dropzone-custom-title">Drag an video here<br/>or <span class="text-highlight">Upload a file</span></p>
                      <p class="text-subtitle">File formats:  MP4, MOV, WMV<br/>File size: Up to 50 MB</p>
                  </div>
                  </vue-dropzone>
              </div>
              <div v-else class="thumbnail-container" style="position: relative;">
                  <!-- TODO: Custom video controller UI-->
                  <video
                      :src="videoUrl"
                      controls
                      class="video-container"/>
                  <div
                  @click="removeVideoThumbnail"
                  class="thumbnail-remove"
                  >
                      <i class="bx bx-x" style="color: white; font-size: 16px;"></i>
                  </div>
              </div>
              <div class="error-container">
                <p class="error-message">{{ errors[0] }}</p>
              </div>
          </div>
          </ValidationProvider>
          <div>
            <div class="thumbnail-header">
              <p>Video Thumbnail</p>
            </div>
            <ValidationProvider
              :rules="isThumbnailFromCopiedMessage ? null : {
                required: {
                  value: true,
                  message: 'Please upload a thumbnail image.',
                },
                multiCustom: {
                  callbacks: [isImageTypeValid, isImageSizeValid],
                  messages: [
                    'The file upload must be in JPG, JPEG, or PNG format',
                    `The maximum file upload size is ${maxThumbnailSize} MB`,
                  ],
                }
              }"
              v-slot="{ errors }"
              ref="thumbnailValidationProviderRef"
            >
            <div v-if="isUploadingThumbnail" class="thumbnail-uploading-container">
              <div class="loading-section">
                <div class="spinner-border" role="status"></div>
              </div>
            </div>
            <div v-else>
              <div class="upload-thumbnail-container">
                <div v-if="thumbnailUrl === null" class="video-thumbnail-dropzone-container">
                  <vue-dropzone
                    ref="thumbnailDropzoneRef"
                    id="video-thumbnail-dropzone"
                    :options="thumbnailDropzoneOptions"
                    :useCustomSlot=true
                    @vdropzone-file-added="handleUploadingThumbnail"
                    @vdropzone-thumbnail="handleThumbnail"
                  >
                    <div class="dropzone-custom-input">
                        <div>
                            <i class="bx bx-image"></i>
                        </div>
                    </div>
                  </vue-dropzone>
                </div>
                <div v-else class="video-thumbnail-container" style="position: relative;">
                  <img :src="thumbnailUrl" alt="thumbnail"/>
                  <div
                    @click="removeThumbnail"
                    class="video-thumbnail-remove"
                  >
                    <i class="bx bx-x" style="color: white; font-size: 16px;"></i>
                  </div>
                </div>
                <div class="error-container">
                  <p class="error-message">{{ errors[0] }}</p>
                </div>
              </div>
            </div>
            </ValidationProvider>
          </div>
        </div>
    </div>
</template>

<script>
import VueDropzone from 'vue2-dropzone';
import 'vue2-dropzone/dist/vue2Dropzone.min.css';
import { ValidationProvider } from 'vee-validate';
import aliceApi from '../../../../alice-api';

export default {
  name: 'VideoMessage',
  components: {
    VueDropzone,
    ValidationProvider,
  },
  props: {
    templateOrder: {
      type: Number,
      required: true,
    },
    copiedVideoMessage: {
      type: Object,
      required: false,
    },
  },
  data() {
    return {
      dropzoneOptions: {
        url: '/',
        acceptedFiles: 'video/mp4,video/mov,video/wmv',
        maxFilesize: 50, // MB
        maxFiles: 1,
        autoProcessQueue: false,
      },
      thumbnailDropzoneOptions: {
        url: '/',
        acceptedFiles: 'image/jpeg,image/png,image/jpg',
        maxFiles: 1,
        autoProcessQueue: false,
      },
      isUploadingVideo: false,
      maxVideoSize: 50, // MB
      videoUrl: null,
      thumbnailUrl: null,
      maxThumbnailSize: 10, // MB
      isUploadingThumbnail: false,
      isVideoFromCopiedMessage: false,
      isThumbnailFromCopiedMessage: false,
    };
  },
  mounted() {
    if (this.copiedVideoMessage && Object.keys(this.copiedVideoMessage).length > 0) {
      this.loadCopiedVideoMessage();
    } else {
      this.$refs.videoValidationProviderRef.validate(this.videoUrl);
      this.$refs.thumbnailValidationProviderRef.validate(this.thumbnailUrl);
    }
  },
  methods: {
    removeVideoTemplate() {
      this.videoUrl = null;
      this.thumbnailUrl = null;
      this.$emit('remove-message-template');
    },
    async handleAddedVideo(file) {
      this.isUploadingVideo = true;
      const { valid } = await this.$refs.videoValidationProviderRef.validate(file);
      if (!valid) {
        this.isUploadingVideo = false;
        this.videoUrl = null;
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'VIDEO');
      const response = await this.sendUploadedMedia(formData);
      if (response) {
        this.videoUrl = response.data.data.filePath;
        this.formatAndEmitVideoResponse();
      }
      this.isUploadingVideo = false;
    },
    isVideoSizeValid(file) {
      const maxSize = this.maxVideoSize * 1024 * 1024;
      return file.size <= maxSize;
    },
    isVideoTypeValid(file) {
      const validTypes = ['video/mp4', 'video/mov', 'video/wmv'];
      return validTypes.includes(file.type);
    },
    async sendUploadedMedia(formData) {
      try {
        const response = await aliceApi.uploadMediaMessage(formData);
        return response;
      } catch (error) {
        console.error('Error uploading file:', error);
        return null;
      }
    },
    async removeVideoThumbnail() {
      this.isVideoFromCopiedMessage = false;
      await this.$nextTick();
      this.videoUrl = null;
      this.$refs.videoValidationProviderRef.validate(this.videoUrl);
      this.$emit('remove-video-message');
    },
    loadCopiedVideoMessage() {
      this.isVideoFromCopiedMessage = true;
      this.isThumbnailFromCopiedMessage = true;
      this.videoUrl = this.copiedVideoMessage.filePath;
      this.thumbnailUrl = this.copiedVideoMessage.previewImageUrl;
      this.formatAndEmitVideoResponse();
    },
    formatAndEmitVideoResponse() {
      const responseData = {
        originalContentUrl: this.videoUrl,
        previewImageUrl: this.thumbnailUrl ? `${this.thumbnailUrl}?w=auto` : null,
      };
      this.$emit('update-video-message', responseData);
    },
    // Thubnail image handling
    async handleUploadingThumbnail(file) {
      this.isUploadingThumbnail = true;
      const { valid } = await this.$refs.thumbnailValidationProviderRef.validate(file);
      if (!valid) {
        this.isUploadingThumbnail = false;
        this.thumbnailUrl = null;
        return;
      }
      const formData = new FormData();
      formData.append('file', file);
      formData.append('type', 'IMAGE');
      const response = await this.sendUploadedMedia(formData);
      if (response) {
        this.thumbnailUrl = response.data.data.filePath;
        this.formatAndEmitVideoResponse();
      } else {
        this.thumbnailUrl = null;
      }
      this.isUploadingThumbnail = false;
    },
    isImageSizeValid(file) {
      const maxSizeBytes = this.maxThumbnailSize * 1024 * 1024;
      return file.size <= maxSizeBytes;
    },
    isImageTypeValid(file) {
      const acceptedTypes = ['image/jpeg', 'image/png', 'image/jpg'];
      return acceptedTypes.includes(file.type);
    },
    handleThumbnail(file, dataUrl) {
      this.thumbnailUrl = dataUrl;
    },
    async removeThumbnail() {
      this.isThumbnailFromCopiedMessage = false;
      await this.$nextTick();
      this.thumbnailUrl = null;
      this.$refs.thumbnailValidationProviderRef.validate(this.thumbnailUrl);
      this.formatAndEmitVideoResponse();
    },
  },
};
</script>

<style lang="scss" scoped>
.video-message-container {
    margin-top: 20px;
    border: 1px solid #DADCE4;
    border-radius: 8px;
    p{
        margin-bottom: 0px;
    }
    .header-message{
        display: flex;
        justify-content: space-between;
        align-items: center;
        background-color: #F5F6FA;
        font-weight: 500;
        font-size: 14px;
        line-height: 24px;
        letter-spacing: 0px;
        color: #0D173C;
        padding: 10px 16px;
        border-top-left-radius: 8px;
        border-top-right-radius: 8px;
        border-bottom: 1px solid #DADCE4;
        i{
            font-size: 16px;
            color: #4B5178;
            cursor: pointer;
        }
    }
    .message-content{
        padding: 20px;
        .dropzone-container{
            width: 250px;
            height: 250px;
            #video-dropzone{
                width: 100%;
                height: 100%;
                border-radius: 8px;
                border: 1px dashed #8286FF;
                background-color: #EBEAFF;
                .dropzone-custom-input{
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    height: 100%;
                    i{
                        font-size: 40px;
                        color: #4848FF;
                        margin-bottom: 19px;
                    }
                    .dropzone-custom-title{
                        font-weight: 500;
                        font-size: 16px;
                        line-height: 120%;
                        letter-spacing: 0px;
                        text-align: center;
                        color: #4B5178;
                        margin-bottom: 24px;
                        .text-highlight{
                            color: #4848FF;
                        }
                    }
                    .text-subtitle{
                        font-weight: 400;
                        font-size: 12px;
                        line-height: 150%;
                        letter-spacing: 0px;
                        text-align: center;
                        color: #868BA6;
                    }
                }
            }
        }
        .thumbnail-container{
            width: 250px;
            height: 250px;
            border-radius: 8px;
            overflow: hidden;
            video{
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
            .thumbnail-remove{
                position: absolute;
                top: 10px;
                right: 10px;
                width: 30px;
                height: 30px;
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: #0D173C7A;
                border-radius: 50%;
                cursor: pointer;
            }
        }
    }
    .uploading-container{
        padding: 20px;
        .loading-section{
            display: flex;
            justify-content: center;
            align-items: center;
            width: 250px;
            height: 250px;
        }
    }
    .thumbnail-header{
      padding-top: 20px;
      padding-left: 4px;
      p{
          font-weight: 500;
          font-size: 14px;
          line-height: 24px;
          letter-spacing: 0px;
          color: #0D173C;
          padding-bottom: 8px;
      }
    }
    .upload-thumbnail-container{
        padding-left: 4px;
        .video-thumbnail-dropzone-container{
            width: 100px;
            height: 100px;
            #video-thumbnail-dropzone{
                width: 100%;
                height: 100%;
                min-height: 100px !important;
                padding: 0px;
                border-radius: 8px;
                border: 1px dashed #8286FF;
                background-color: #EBEAFF;
                .dropzone-custom-input{
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                    align-items: center;
                    height: 100%;
                    i{
                        font-size: 40px;
                        color: #4848FF;
                        margin-bottom: 19px;
                    }
                }
            }
        }
        .video-thumbnail-container{
            border-radius: 8px;
            overflow: hidden;
            width: 100px;
            height: 100px;
            img{
                width: 100%;
                height: 100%;
                object-fit: cover;
            }
            .video-thumbnail-remove{
                position: absolute;
                top: 10px;
                right: 10px;
                width: 30px;
                height: 30px;
                display: flex;
                justify-content: center;
                align-items: center;
                background-color: #0D173C7A;
                border-radius: 50%;
                cursor: pointer;
            }
        }
    }
    .upload-media-container{
        display: flex;
    }
    .error-container{
      height: 28px;
      .error-message{
        font-weight: 400;
        font-size: 12px;
        line-height: 150%;
        letter-spacing: 0px;
        color: #EF6767;
        height: 18px;
        width: max-content;
        padding-top: 10px;
      }
    }
    .thumbnail-uploading-container{
      .loading-section{
          display: flex;
          justify-content: center;
          align-items: center;
          width: 100px;
          height: 100px;
      }
}
}
</style>
